import { useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { SeoInternalLink } from '@/types/api/seo';
import { PageHeader, SectionCard } from '@/components/shared';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import MetricCard from '@/trio-ui/MetricCard';

type LinkOpportunity = {
  keyword: string;
  url: string;
  position: number | null;
  impressions: number;
  inboundLinks: number;
};

type LinkStats = {
  totalLinks: number;
  uniqueSourcePages: number;
  uniqueTargetPages: number;
  orphanPages: string[];
  topLinkedPages: Array<{ url: string; inboundCount: number }>;
};

type Props = {
  stats: LinkStats | null;
  links: SeoInternalLink[];
  linkOpportunities: LinkOpportunity[];
  isLoading: boolean;
};

const formatPath = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const SeoInternalLinksComponent = function ({
  stats,
  links,
  linkOpportunities,
  isLoading,
}: Props): JSX.Element {
  const [tab, setTab] = useState<'overview' | 'graph'>('overview');

  if (isLoading || !stats) {
    return (
      <Box sx={{}}>
        <PageHeader title="Internal Links" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading link data...' : 'Run a site audit first to build the internal link graph.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  const avgLinksPerPage = stats.uniqueSourcePages > 0
    ? Math.round(stats.totalLinks / stats.uniqueSourcePages)
    : 0;

  return (
    <Box sx={{}}>
      <PageHeader title="Internal Links" />

      {/* Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
        <MetricCard label="Total Links" value={stats.totalLinks.toLocaleString()} color="#054AD9" />
        <MetricCard label="Pages with Links" value={stats.uniqueSourcePages} color="#054AD9" />
        <MetricCard label="Orphan Pages" value={stats.orphanPages.length} color={stats.orphanPages.length > 0 ? '#EF5B5B' : '#0D9D58'} />
        <MetricCard label="Avg Links/Page" value={avgLinksPerPage} color="#054AD9" />
      </Box>

      {/* Tab navigation */}
      <Box sx={{ display: 'flex', gap: '4px', mb: '20px', borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
        {(['overview', 'graph'] as const).map((t) => (
          <Box
            key={t}
            onClick={() => setTab(t)}
            sx={{
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: tab === t ? 500 : 400,
              color: tab === t ? '#054AD9' : '#71717A',
              borderBottom: `2px solid ${tab === t ? '#054AD9' : 'transparent'}`,
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
              '&:hover': { color: '#054AD9' },
            }}
          >
            {t === 'overview' ? 'Overview' : `All Links (${links.length})`}
          </Box>
        ))}
      </Box>

      {/* ============ OVERVIEW TAB ============ */}
      {tab === 'overview' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Link Opportunities */}
          <SectionCard
            title="Link Opportunities"
            icon={<TrendingUpIcon sx={{ fontSize: 18, color: '#054AD9' }} />}
            badge={linkOpportunities.length > 0 ? (
              <Chip label={`${linkOpportunities.length} found`} size="small" sx={{ fontSize: 11, height: 20 }} />
            ) : undefined}
          >
            {linkOpportunities.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                All tracked keyword pages have good internal link coverage.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {linkOpportunities.slice(0, 8).map((opp) => (
                  <Box key={opp.keyword} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid hsla(0,0%,0%,0.04)' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500}>{opp.keyword}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatPath(opp.url)} — #{opp.position?.toFixed(1) ?? '?'} — {opp.impressions.toLocaleString()} imp.
                      </Typography>
                    </Box>
                    <Chip
                      label={`${opp.inboundLinks} links`}
                      size="small"
                      color={opp.inboundLinks === 0 ? 'error' : 'warning'}
                      sx={{ fontSize: 11, height: 20, flexShrink: 0, ml: 1 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>

          {/* Orphan Pages */}
          <SectionCard
            title="Orphan Pages"
            icon={<WarningAmberIcon sx={{ fontSize: 18, color: stats.orphanPages.length > 0 ? '#EF5B5B' : '#0D9D58' }} />}
            badge={stats.orphanPages.length > 0 ? (
              <Chip label={stats.orphanPages.length} size="small" color="error" sx={{ fontSize: 11, height: 20 }} />
            ) : undefined}
          >
            {stats.orphanPages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No orphan pages found — all pages have at least one inbound link.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {stats.orphanPages.slice(0, 10).map((url) => (
                  <Box key={url} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatPath(url)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      No pages link here — Google may not discover this page.
                    </Typography>
                  </Box>
                ))}
                {stats.orphanPages.length > 10 && (
                  <Typography variant="caption" color="text.secondary">
                    + {stats.orphanPages.length - 10} more
                  </Typography>
                )}
              </Box>
            )}
          </SectionCard>

          {/* Most Linked Pages */}
          <SectionCard
            title="Most Linked Pages"
            icon={<LinkIcon sx={{ fontSize: 18, color: '#054AD9' }} />}
          >
            {stats.topLinkedPages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No link data yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {stats.topLinkedPages.slice(0, 10).map((page, i) => (
                  <Box key={page.url} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid hsla(0,0%,0%,0.04)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 20, flexShrink: 0 }}>
                        {i + 1}.
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatPath(page.url)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={500} color="#054AD9" sx={{ flexShrink: 0, ml: 1 }}>
                      {page.inboundCount}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </Box>
      )}

      {/* ============ ALL LINKS TAB ============ */}
      {tab === 'graph' && (
        <Table
          data={links}
          renderHeader={
            <Tr>
              <Th>Source Page</Th>
              <Th>Target Page</Th>
              <Th>Anchor Text</Th>
            </Tr>
          }
          renderRow={(link) => (
            <Tr key={link.id}>
              <Td>
                <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={link.sourceUrl}>
                  {formatPath(link.sourceUrl)}
                </Typography>
              </Td>
              <Td>
                <Typography variant="body2" sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={link.targetUrl}>
                  {formatPath(link.targetUrl)}
                </Typography>
              </Td>
              <Td>
                {link.anchorText ? (
                  <Chip label={link.anchorText} size="small" sx={{ fontSize: 11, maxWidth: 200 }} />
                ) : (
                  <Typography variant="caption" color="text.disabled">—</Typography>
                )}
              </Td>
            </Tr>
          )}
          renderEmptyState={
            <Tr>
              <Td colSpan={3} textAlign="center">
                No internal links found. Run a site audit to build the link graph.
              </Td>
            </Tr>
          }
        />
      )}
    </Box>
  );
};

export default SeoInternalLinksComponent;
